import React, { useState, useEffect } from 'react';

import { useGetArterialThickness } from 'api/services';
import ChartModal from 'components/ChartModal/ChartModal';
import { chartsKey } from 'utils/constants';

import { getGradientBackground } from './utils/chartColorizing';
import {
	getChartMaxValue,
	getChartMinValue,
	defaultChartAxisValues,
	handleChartData,
	increaseOrDecreaseMaxYThreshold,
	strip,
} from './utils/chartHelpers';
import {
	defaultConfig,
	ticksCallbackMM,
	tooltipCallbackMM,
	styledChartElements,
} from './utils/customViewChart';

const ArterialThickness = ({ closeChart, isOpen }) => {
	const [data, setData] = useState({});
	const [chartConfig, setChartConfig] = useState(null);
	const [labelsData, setLabelsData] = useState(undefined);

	const { data: thicknessData, loading } = useGetArterialThickness();
	const isDataAvailable = thicknessData?.data && thicknessData.data.length > 0;

	useEffect(() => {
		if (isDataAvailable) {
			const dataAdapted = handleChartData(thicknessData.data);

			setData(dataAdapted);
			setLabelsData(dataAdapted?.labels);
			handleChartConfig();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [thicknessData]);

	const handleChartConfig = () => {
		let minY = defaultChartAxisValues[chartsKey.arterialThickness].minY;
		let maxY = defaultChartAxisValues[chartsKey.arterialThickness].maxY;

		const minValueOfY = getChartMinValue(thicknessData.data, 'value');
		const maxValueOfY = getChartMaxValue(thicknessData.data, 'value');

		// in order to have clearer/better overview of the chart modal
		// we are increasing/decreasing the max/min value of Y
		if (maxValueOfY && maxValueOfY >= maxY) {
			maxY = strip(maxValueOfY + increaseOrDecreaseMaxYThreshold.small, 2);
		}

		if (minValueOfY && minValueOfY <= minY) {
			minY = strip(minValueOfY - increaseOrDecreaseMaxYThreshold.small, 1);
		}

		const config = {
			...defaultConfig,
			plugins: {
				...defaultConfig.plugins,
				tooltip: {
					...defaultConfig.plugins.tooltip,
					callbacks: { label: tooltipCallbackMM },
				},
			},
			elements: styledChartElements(thicknessData.data.length - 1),
			scales: {
				...defaultConfig.scales,
				y: {
					...defaultConfig.scales.y,
					min: minY,
					max: maxY,
					ticks: {
						...defaultConfig.scales.y.ticks,
						callback: ticksCallbackMM,
						stepSize: 0.1,
						maxTicksLimit: 10,
					},
				},
			},
		};

		setChartConfig(config);
	};

	const getChartData = (canvas) => {
		const gradient = getGradientBackground(canvas);

		return {
			...data,
			datasets: [
				{
					data: data?.datasets[0]?.data,
					fill: true,
					backgroundColor: gradient,
				},
			],
		};
	};

	return (
		<ChartModal
			loading={loading}
			data={getChartData}
			labelsData={labelsData}
			closeChart={closeChart}
			chartConfig={chartConfig}
			isOpen={isOpen}
		/>
	);
};

export default ArterialThickness;
